import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { TypeGetShippingLabelsParams, TypeGetShippingLabelsResponse } from '~/types/delivery-agency.types';

/**
 * [운송장 관리] 운송장 리스트 조회 API
 */
export const fetchShippingLabels = async (
  params: TypeGetShippingLabelsParams
): Promise<TypeGetShippingLabelsResponse> => {
  const { data } = await axiosInstance.get<TypeGetShippingLabelsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_SHIPPING_LABELS,
    { params }
  );
  return data;
};

type UseGetShippingLabelsQueryOptions = {
  params: TypeGetShippingLabelsParams;
  enabled?: boolean;
};

/**
 * [운송장 관리] 운송장 리스트 조회 Query Hook
 */
export const useGetShippingLabelsQuery = ({ params, enabled = true }: UseGetShippingLabelsQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.shippingLabels(params),
    queryFn: () => fetchShippingLabels(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

/**
 * [운송장 관리] 운송장 리스트 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다.
 */
registerMockAPI<TypeGetShippingLabelsResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/shipping-labels',
  label: '운송장 관리/운송장 리스트',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: [
          {
            shippingLabelId: 2001,
            shipperName: '컬리 화주사',
            shippingLabelStatus: { code: 'OUTBOUND_READY', description: '출고 대기' },
            statusUpdatedAt: '2026-07-20T09:12:00',
            shippingLabelNumber: '210-C0-JA9LAK0140001-0001',
            isPrinted: false,
            outboundCode: 'JA9LAK0140001',
            clientOrderCodes: ['ORD-20260720-0001'],
            estimatedDeliveryDate: '2026-07-21',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCode: { code: 'NM', description: '넥스트마일' },
            receiverName: '홍길동',
            ordererName: '홍길동',
            productName: '유기농 우유 900ml 외 2건',
            createdAt: '2026-07-20T08:30:00',
          },
          {
            shippingLabelId: 2002,
            shipperName: '컬리 화주사',
            shippingLabelStatus: { code: 'OUTBOUND_PROCESSING', description: '출고 진행중' },
            statusUpdatedAt: '2026-07-20T10:05:00',
            shippingLabelNumber: '210-C0-JA9LAK0140002-0001',
            isPrinted: true,
            outboundCode: 'JA9LAK0140002',
            clientOrderCodes: ['ORD-20260720-0002'],
            estimatedDeliveryDate: '2026-07-21',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCode: { code: 'CJT', description: 'CJ' },
            receiverName: '김수령',
            ordererName: '김주문',
            productName: '제철 사과 5kg',
            createdAt: '2026-07-20T08:35:00',
          },
          {
            shippingLabelId: 2003,
            shipperName: '컬리 화주사',
            shippingLabelStatus: { code: 'ON_THE_WAY', description: '배송 중' },
            statusUpdatedAt: '2026-07-20T14:20:00',
            shippingLabelNumber: '210-C0-JA9LAK0140003-0001',
            isPrinted: true,
            outboundCode: 'JA9LAK0140003',
            clientOrderCodes: ['ORD-20260719-0011', 'ORD-20260719-0012'],
            estimatedDeliveryDate: '2026-07-20',
            deliveryType: { code: 'DAY', description: '하루배송' },
            courierCode: { code: 'LTT', description: '롯데' },
            receiverName: '이배송',
            ordererName: '이주문',
            productName: '탄산수 500ml x24',
            createdAt: '2026-07-19T18:00:00',
          },
          {
            shippingLabelId: 2004,
            shipperName: '컬리 화주사',
            shippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
            statusUpdatedAt: '2026-07-19T06:40:00',
            shippingLabelNumber: '210-C0-JA9LAK0140004-0001',
            isPrinted: true,
            outboundCode: 'JA9LAK0140004',
            clientOrderCodes: ['ORD-20260718-0021'],
            estimatedDeliveryDate: '2026-07-19',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCode: { code: 'NM', description: '넥스트마일' },
            receiverName: '박완료',
            ordererName: '박주문',
            productName: '샐러드 도시락 세트',
            createdAt: '2026-07-18T17:10:00',
          },
          {
            shippingLabelId: 2005,
            shipperName: '컬리 화주사',
            shippingLabelStatus: { code: 'DELIVERY_FAILED', description: '배송 실패' },
            statusUpdatedAt: '2026-07-19T07:15:00',
            shippingLabelNumber: '210-C0-JA9LAK0140005-0001',
            isPrinted: true,
            outboundCode: 'JA9LAK0140005',
            clientOrderCodes: ['ORD-20260718-0031'],
            estimatedDeliveryDate: '2026-07-19',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCode: { code: 'CJT', description: 'CJ' },
            receiverName: '최실패',
            ordererName: '최주문',
            productName: '냉동 만두 1.4kg',
            createdAt: '2026-07-18T17:20:00',
          },
        ],
        pagination: {
          cursor: 1,
          listView: 50,
          listTotal: 5,
        },
      },
    },
    {
      name: '데이터 없음',
      response: {
        data: [],
        pagination: {
          cursor: 1,
          listView: 50,
          listTotal: 0,
        },
      },
    },
  ],
});
